import { Badge, Center } from '@mantine/core';
/* eslint-disable max-statements */
import { modals } from '@mantine/modals';
import { IconAlertTriangleFilled, IconBell } from '@tabler/icons-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import { Customer, EnvironmentCountries, EORIGIN_DATE_FORMAT_WITH_TIME, Template, Transit, TransitGroup, TransitStatus } from '@e-origin/shared';

import { copyIcon, customsSyncIcon, deleteIcon, downloadIcon, finalizeIcon, reportIcon, sendIcon, viewIcon } from '@assets';
import { TableActions, TableColumnProps, TableNotificationIndicator } from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { DeclarationFlowBackgroundColors } from '@enums';
import {
  useCustomsSyncTransit,
  useDeleteTransit,
  useDownloadAllHouseConsignmentItems,
  useDownloadTransitFollowLetter,
  useDownloadTransitReport,
  useGenerateTransitFollowLetter,
  useGenerateTransitReport,
  useSendOneTransit,
  useTriggerTransitNotification,
} from '@hooks/transit';
import { useSettings } from '@hooks/useSettings';
import { TableCellInLineContent } from '../../declarations/components/DeclarationGroupList/DeclarationsGroupTable/TableCellInLineContent';
import { canTransitBeAmended } from '../helpers';
import { transitModalManager } from './transitModalManager';
import { TransitStatusIndicator } from './TransitStatusIndicator';

export const useTransitsTableColumns = ({ transitView, pagination }) => {
  const navigate = useNavigate();
  const { data: settings } = useSettings();

  const isDeparture = transitView === TransitGroup.DEPARTURE;
  const customsSyncMutation = useCustomsSyncTransit();

  const triggerTransitNotification = useTriggerTransitNotification();
  const downloadTransitFollowLetterMutation = useDownloadTransitFollowLetter();
  const downloadTransitReportMutation = useDownloadTransitReport();
  const generateTransitFollowLetterMutation = useGenerateTransitFollowLetter();
  const generateTransitReportMutation = useGenerateTransitReport();
  const sendOneMutation = useSendOneTransit();
  const deleteTransitMutation = useDeleteTransit();
  const downloadAllHouseConsignmentItemsMutation = useDownloadAllHouseConsignmentItems();
  const overflowEllipsisStyle = { maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', textWrapMode: 'nowrap' };

  const openSendConfirmationModal = (transitId: string) => {
    modals.openConfirmModal({
      title: 'Confirm sending arrival transit',
      children: <div>Has transport arrived? Please confirm sending notification to customs.</div>,
      labels: { confirm: 'Send', cancel: 'Cancel' },
      confirmProps: { color: 'blue' },
      onConfirm: () => sendOneMutation.mutate({ id: transitId }),
    });
  };

  const actions = (row: Transit) => {
    const finalizeAllowed =
      transitView === TransitGroup.ARRIVAL &&
      row.TransitOperation.simplifiedProcedure === '1' &&
      ![TransitStatus.NOT_SENT, TransitStatus.REJECTED, TransitStatus.SENT, TransitStatus.SENDING].includes(row.generalInfo?.status);

    const departureStatusesAllowedForCustomsSync = [TransitStatus.SENT, TransitStatus.ACCEPTED, TransitStatus.RELEASED];
    const arrivalStatusesForCustomsSync = [TransitStatus.SENT, TransitStatus.ACCEPTED, TransitStatus.REMARKS_SENT];
    const isRejectedInPost =
      row.TransitOperation.additionalDeclarationType === 'D' && row.generalInfo.status === TransitStatus.REJECTED && row.customsState?.flow === 'POST';
    const isAcceptedInPre =
      row.TransitOperation.additionalDeclarationType === 'D' && row.generalInfo.status === TransitStatus.ACCEPTED && row.customsState?.flow === 'PRE';
    const canSendPresentationNotification = TransitGroup.DEPARTURE === transitView && (isAcceptedInPre || isRejectedInPost);

    return [
      {
        icon: viewIcon,
        text: 'View details',
        onClick: () => {
          navigate(`/transits/${row._id}`);
        },
        preventDefaultClick: true,
      },
      {
        if: TransitStatus.NOT_SENT === row.generalInfo?.status,
        icon: deleteIcon,
        text: 'Delete transit',
        onClick: () => transitModalManager.openDeleteTransit(row._id, (transitId) => deleteTransitMutation.mutate({ id: transitId })),
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        if:
          ([TransitStatus.NOT_SENT, TransitStatus.REJECTED, TransitStatus.UNLOAD_PERMISSION].includes(row.generalInfo?.status) ||
            canSendPresentationNotification) &&
          !finalizeAllowed,
        icon: sendIcon,
        text: 'Send',
        onClick: async () => {
          if (row.generalInfo.group === TransitGroup.ARRIVAL && [TransitStatus.NOT_SENT, TransitStatus.REJECTED].includes(row.generalInfo?.status)) {
            openSendConfirmationModal(row._id);
          } else {
            sendOneMutation.mutate({ id: row._id });
          }
        },
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        if: finalizeAllowed,
        icon: finalizeIcon,
        text: 'Finalize',
        onClick: () => transitModalManager.openFinalize(row._id),
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        if:
          settings?.country === EnvironmentCountries.BE &&
          (row.generalInfo.group === TransitGroup.DEPARTURE
            ? departureStatusesAllowedForCustomsSync.includes(row.generalInfo?.status)
            : arrivalStatusesForCustomsSync.includes(row.generalInfo?.status)),
        icon: customsSyncIcon,
        text: 'Customs sync',
        onClick: () => {
          customsSyncMutation.mutate({ id: row._id });
        },
        disableAfterClick: false,
        preventDefaultClick: true,
      },
    ];
  };

  const secondaryActions = (row: Transit) => {
    return [
      {
        icon: reportIcon,
        text: 'Generate customs report',
        onClick: () => {
          generateTransitReportMutation.mutate({ id: row._id });
        },
        preventDefaultClick: true,
      },
      {
        icon: reportIcon,
        text: 'Generate follow letter',
        onClick: () => {
          generateTransitFollowLetterMutation.mutate({ id: row._id });
        },
        preventDefaultClick: true,
      },
      {
        if: transitView === TransitGroup.DEPARTURE && !!row.TransitOperation?.MRN,
        icon: reportIcon,
        text: 'Download customs report',
        onClick: () => {
          downloadTransitReportMutation.mutate({ id: row._id });
        },
        preventDefaultClick: true,
      },
      {
        if: transitView === TransitGroup.DEPARTURE && !!row.TransitOperation?.MRN,
        icon: reportIcon,
        text: 'Download follow letter',
        onClick: () => {
          downloadTransitFollowLetterMutation.mutate({ id: row._id });
        },
        preventDefaultClick: true,
      },
      {
        icon: downloadIcon,
        text: 'Download items summary',
        onClick: () => {
          downloadAllHouseConsignmentItemsMutation.mutate({ id: row._id, name: row.name });
        },
        preventDefaultClick: true,
      },
      {
        if: transitView === TransitGroup.DEPARTURE,
        icon: copyIcon,
        text: canTransitBeAmended(row) ? 'Duplicate or amend transit' : 'Duplicate transit',
        onClick: () => transitModalManager.openDuplicate(row),
        preventDefaultClick: true,
      },
    ];
  };

  const handleTableAction = async (actionType: string, row: Transit) => {
    const [, messageType] = /generateNCTSResponse-(.*)/.exec(actionType);
    triggerTransitNotification.mutate({ transitId: row._id, messageType });
  };

  const buildColumns = (transitViewParam): TableColumnProps<Transit>[] => [
    {
      name: <IconBell />,
      cell: (row) => (
        <TableNotificationIndicator
          notificationsPending={row.customsState?.notifications?.filter((notification) => notification.read === false).length || 0}
          navigateTo={`/transits/${row._id}/customs-state`}
        />
      ),
    },
    {
      name: 'Status',
      selector: 'generalInfo.status',
      sortable: true,
      cell: (row) => (
        <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
          <TransitStatusIndicator
            status={row.generalInfo.status}
            isDeparture={isDeparture}
            releaseIndicator={row?.customsState?.releaseDestination?.releaseIndicator}
          />
        </div>
      ),
    },
    {
      name: 'Flow/System',
      cell: (row) => (
        <TableCellInLineContent>
          {row.customsState?.sentTo && (
            <Badge size="md" color={DeclarationFlowBackgroundColors[row.customsState?.flow ?? 'POST']}>
              {row.customsState?.flow?.toLowerCase() ?? ''}
              {row.customsState?.sentTo}
            </Badge>
          )}
        </TableCellInLineContent>
      ),
      hide: settings?.country === EnvironmentCountries.BE || transitViewParam === TransitGroup.ARRIVAL,
    },
    {
      name: 'Transit ID',
      selector: 'counter',
      cell: (row) => row.counter,
      sortable: true,
    },
    {
      name: 'Name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Decl. type',
      width: '120px',
      cell: (row) => `${row.TransitOperation.declarationType ?? ''} ${row.TransitOperation.additionalDeclarationType ?? ''}`,
      style: overflowEllipsisStyle,
    },
    {
      name: 'LRN',
      selector: 'TransitOperation.LRN',
      cell: (row) => row.TransitOperation.LRN,
      sortable: true,
    },
    {
      name: 'MRN',
      selector: 'TransitOperation.MRN',
      cell: (row, rowIdx) => <span data-pw={`cell-mrn-${rowIdx}`}>{row.TransitOperation.MRN}</span>,
      sortable: true,
    },
    {
      name: 'Holder of transit proc.',
      selector: 'HolderOfTheTransitProcedure.name',
      cell: (row) => row.HolderOfTheTransitProcedure?.name,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Incident',
      cell: (row) => {
        if (Number(row.TransitOperation?.incidentFlag) === 1) {
          return (
            <Center>
              <IconAlertTriangleFilled color="orange" />
            </Center>
          );
        }
      },
      style: { textAlign: 'center' },
      selector: 'TransitOperation.incidentFlag',
      sortable: true,
    },
    {
      name: 'Recovery',
      cell: (row) => {
        if (row.customsState?.recoveryNotification?.recoveryNotificationDate) {
          return (
            <Center>
              <IconAlertTriangleFilled color="orange" />
            </Center>
          );
        }
      },
    },
    {
      name: 'Discrepancies',
      cell: (row) => {
        if (Number(row.UnloadingRemark?.conform) === 0 || row.customsState?.discrepancies?.length) {
          return (
            <Center>
              <IconAlertTriangleFilled color="orange" />
            </Center>
          );
        }
      },
      selector: 'UnloadingRemark.conform',
    },
    {
      name: 'C.O. of Departure',
      cell: (row) => row.CustomsOfficeOfDeparture?.referenceNumber,
      selector: 'CustomsOfficeOfDeparture.referenceNumber',
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'C.O. of Destination',
      cell: (row) => row.CustomsOfficeOfDestinationDeclared?.referenceNumber,
      selector: 'CustomsOfficeOfDestinationDeclared.referenceNumber',
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Amount to be covered',
      selector: 'generalInfo.totalAmountGuaranteeToBeCovered',
      cell: (row) => row.generalInfo.totalAmountGuaranteeToBeCovered,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Packages',
      selector: 'Consignment.totalNumberOfPackages',
      cell: (row) => row.Consignment.totalNumberOfPackages,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Gross Mass',
      selector: 'Consignment.grossMass',
      cell: (row) => row.Consignment.grossMass,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Seals',
      selector: 'Consignment.totalNumberOfSeals',
      cell: (row) => row.Consignment.totalNumberOfSeals,
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Deadline',
      cell: (row) => row.CustomsOfficeOfTransitDeclared?.[0]?.arrivalDateAndTimeEstimated || 'N/A',
      selector: 'CustomsOfficeOfTransitDeclared[0].arrivalDateAndTimeEstimated',
      sortable: true,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Customer',
      cell: (row) => (row.customer as unknown as Customer)?.name,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Template',
      cell: (row) => (row.template as unknown as Template)?.name,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Last update',
      selector: 'updatedAt',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row, rowIdx) => (
        <TableActions
          actions={actions(row)}
          secondaryActions={secondaryActions(row)}
          onClick={(param?: string) => handleTableAction(param, row)}
          disableAfterClick={true}
          transitView={transitViewParam}
          showDebugMenu={true}
          moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
          data-pw={row.name}
        />
      ),
    },
  ];

  return buildColumns(transitView);
};
